import { Center, Paper, Title, Text, Button, Stack, Modal } from '@mantine/core';
import { pageWrapper } from '@/Styles';
import { useState, useEffect, useRef, useCallback } from 'react';
import { SessionAuth, getUserId } from 'supertokens-auth-react/recipe/session';
import { getAppSubscriptionInfosByUser, getUnityHandoffTokens } from '../Api';
import { hasValidWebCatalogueLicense } from '../utils/licenseUtils';
import { useTranslation } from 'react-i18next';
import { IconMail, IconAlertCircle } from '@tabler/icons-react';
import {
  getUnityIframeOrigin,
  isUnitySessionReadyMessage,
  postUnitySessionToIframe,
} from '@/services/unitySessionHandoff';

type EcomProps = {
  /** Full URL of the Unity WebGL build to embed (stable vs. beta). */
  iframeUrl: string;
  /** Outer container height; clips the fixed-height iframe per build. */
  containerHeight?: string;
};

export function Ecom({ iframeUrl, containerHeight = '900px' }: EcomProps) {
  const feedbackFormUrl = 'https://portfolio-rbtp.notion.site/ebd/35813aadde4480a4a815db8128491b0d';
  const [isLoadingLicense, setIsLoadingLicense] = useState(true);
  const [hasValidLicense, setHasValidLicense] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const checkLicense = async () => {
      try {
        const userId = await getUserId();
        if (!userId) {
          setHasValidLicense(false);
          setIsLoadingLicense(false);
          return;
        }

        const licenses = await getAppSubscriptionInfosByUser(userId);
        const isValid = hasValidWebCatalogueLicense(licenses);
        setHasValidLicense(isValid);
      } catch (error) {
        console.error('Error checking license:', error);
        setHasValidLicense(false);
      } finally {
        setIsLoadingLicense(false);
      }
    };

    checkLicense();
  }, []);

  const postSessionToUnity = useCallback(async () => {
    try {
      // Single source of truth: handoff API (access + refresh + front-token when available).
      const handoff = await getUnityHandoffTokens();
      const ok = postUnitySessionToIframe(iframeRef.current, iframeUrl, handoff.tokens);
      if (!ok) {
        console.warn('No access token or Unity iframe not ready for session handoff');
      }
    } catch (error) {
      console.error('Failed to post session to Unity iframe:', error);
    }
  }, [iframeUrl]);

  // Re-send tokens on tab focus and on a gentle interval (access tokens expire).
  useEffect(() => {
    if (!hasValidLicense) return;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void postSessionToUnity();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const intervalId = window.setInterval(
      () => {
        void postSessionToUnity();
      },
      4 * 60 * 1000
    );

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [hasValidLicense, postSessionToUnity]);

  // Unity build may request tokens once its JS bridge is ready.
  useEffect(() => {
    if (!hasValidLicense) return;

    const allowedOrigin = getUnityIframeOrigin(iframeUrl);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return;
      if (isUnitySessionReadyMessage(event.data)) {
        void postSessionToUnity();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [hasValidLicense, iframeUrl, postSessionToUnity]);

  const handleContactUs = () => {
    const subject = encodeURIComponent(t('ecom.licenseRequired.emailSubject'));
    const body = encodeURIComponent(t('ecom.licenseRequired.emailBody'));
    window.location.href = `mailto:service@robotop.de?subject=${subject}&body=${body}`;
  };

  if (isLoadingLicense) {
    return (
      <SessionAuth>
        <Center style={pageWrapper}>
          <Text>{t('ecom.licenseRequired.loading')}</Text>
        </Center>
      </SessionAuth>
    );
  }

  if (!hasValidLicense) {
    return (
      <SessionAuth>
        <Center style={pageWrapper}>
          <Paper
            shadow="md"
            p="xl"
            radius="md"
            withBorder
            style={{ maxWidth: '600px', width: '100%' }}
          >
            <Stack gap="md" align="center">
              <IconAlertCircle size={64} color="orange" />
              <Title order={2} ta="center">
                {t('ecom.licenseRequired.title')}
              </Title>
              <Text ta="center" c="dimmed" size="lg">
                {t('ecom.licenseRequired.description')}
              </Text>
              <Button
                leftSection={<IconMail size={18} />}
                onClick={handleContactUs}
                size="lg"
                variant="filled"
              >
                {t('ecom.licenseRequired.contactUs')}
              </Button>
              <Text ta="center" size="sm" c="dimmed" mt="md">
                {t('ecom.licenseRequired.footer')}
              </Text>
            </Stack>
          </Paper>
        </Center>
      </SessionAuth>
    );
  }

  return (
    <SessionAuth>
      <Center style={pageWrapper}>
        <div style={{ overflow: 'hidden', height: containerHeight }}>
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            width="1500"
            height="900"
            title="ROBOPlaner"
            style={{ border: 0 }}
            onLoad={() => {
              void postSessionToUnity();
            }}
          />
        </div>
        <Button
          variant="light"
          leftSection={<IconMail size={18} />}
          onClick={() => setIsFeedbackModalOpen(true)}
        >
          {t('ecom.feedback.button')}
        </Button>
        <Modal
          opened={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          title={t('ecom.feedback.title')}
          centered
          size="xl"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t('ecom.feedback.description')}
            </Text>
            <iframe
              src={feedbackFormUrl}
              width="100%"
              height="600"
              style={{ border: 0 }}
              allowFullScreen
              title={t('ecom.feedback.formTitle')}
            />
          </Stack>
        </Modal>
      </Center>
    </SessionAuth>
  );
}
